import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, GitBranch, ArrowRight, StopCircle } from 'lucide-react';
import { Button } from '@centraoneplus/ui';
import type { Action, ConditionalConfig, SwitchConfig, SwitchCase, ConditionGroup } from '../../services/workflow.service';
import { ActionType } from '../../services/workflow.service';
import ActionConfigPanel, { getDefaultConfig } from './ActionConfigPanel';
import ConditionBuilder from './ConditionBuilder';

// Shared styles
const inputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500';
const selectClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white';

// Available action types for nested actions (excludes branching to prevent deep nesting)
const NESTED_ACTION_TYPES: ActionType[] = [
  ActionType.SET_STATUS,
  ActionType.SET_PRIORITY,
  ActionType.ASSIGN_AGENT,
  ActionType.ASSIGN_TEAM,
  ActionType.AUTO_ASSIGN,
  ActionType.ADD_TAG,
  ActionType.REMOVE_TAG,
  ActionType.SEND_EMAIL,
  ActionType.SEND_NOTIFICATION,
  ActionType.TRIGGER_WEBHOOK,
  ActionType.TRIGGER_N8N_WORKFLOW,
  ActionType.CREATE_TICKET,
  ActionType.STOP_WORKFLOW,
];

const ACTION_LABELS: Record<ActionType, string> = {
  [ActionType.SET_STATUS]: 'Set Status',
  [ActionType.SET_PRIORITY]: 'Set Priority',
  [ActionType.ASSIGN_AGENT]: 'Assign Agent',
  [ActionType.ASSIGN_TEAM]: 'Assign Team',
  [ActionType.AUTO_ASSIGN]: 'Auto-assign Agent',
  [ActionType.ADD_TAG]: 'Add Tag',
  [ActionType.REMOVE_TAG]: 'Remove Tag',
  [ActionType.SEND_EMAIL]: 'Send Email',
  [ActionType.SEND_NOTIFICATION]: 'Send Notification',
  [ActionType.TRIGGER_WEBHOOK]: 'Trigger Webhook',
  [ActionType.TRIGGER_N8N_WORKFLOW]: 'n8n Workflow',
  [ActionType.CREATE_TICKET]: 'Create Ticket',
  [ActionType.STOP_WORKFLOW]: 'Stop Workflow',
  // Branching types (for completeness, though not in nested list)
  [ActionType.CONDITIONAL]: 'If/Else Condition',
  [ActionType.SWITCH]: 'Switch/Case',
};

// Common field options for switch
const SWITCH_FIELD_OPTIONS = [
  { value: 'entity.status', label: 'Status' },
  { value: 'entity.priority', label: 'Priority' },
  { value: 'entity.channel', label: 'Channel' },
  { value: 'entity.ticketTypeId', label: 'Ticket Type' },
];

interface NestedActionListProps {
  actions: Action[];
  onChange: (actions: Action[]) => void;
  label: string;
  color?: 'green' | 'red' | 'blue' | 'gray';
}

function NestedActionList({ actions, onChange, label, color = 'gray' }: NestedActionListProps) {
  const [expanded, setExpanded] = useState(true);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  const colorClasses = {
    green: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20',
    red: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
    blue: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20',
    gray: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800',
  };

  const headerClasses = {
    green: 'text-green-700 dark:text-green-300',
    red: 'text-red-700 dark:text-red-300',
    blue: 'text-blue-700 dark:text-blue-300',
    gray: 'text-gray-700 dark:text-gray-300',
  };

  const addAction = () => {
    const defaultType = ActionType.SET_STATUS;
    onChange([...actions, { type: defaultType, config: getDefaultConfig(defaultType) }]);
  };

  const removeAction = (index: number) => {
    const newActions = [...actions];
    newActions.splice(index, 1);
    onChange(newActions);
  };

  const updateAction = (index: number, action: Action) => {
    const newActions = [...actions];
    newActions[index] = action;
    onChange(newActions);
  };

  return (
    <div className={`rounded-lg border ${colorClasses[color]} p-3`}>
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className={`w-4 h-4 ${headerClasses[color]}`} />
          ) : (
            <ChevronRight className={`w-4 h-4 ${headerClasses[color]}`} />
          )}
          <span className={`text-sm font-medium ${headerClasses[color]}`}>
            {label} ({actions.length} action{actions.length !== 1 ? 's' : ''})
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            addAction();
          }}
        >
          <Plus className="w-3 h-3 mr-1" />
          Add
        </Button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2">
          {actions.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic pl-6">
              No actions - click Add to create one
            </p>
          ) : (
            actions.map((action, index) => (
              <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-3 ml-4 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <button
                      type="button"
                      onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                      className={`${selectClass} flex items-center justify-between text-left`}
                    >
                      <span>{ACTION_LABELS[action.type] || action.type}</span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${openDropdown === index ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === index && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
                        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {NESTED_ACTION_TYPES.map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => {
                                updateAction(index, { type, config: getDefaultConfig(type) });
                                setOpenDropdown(null);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-500 ${
                                action.type === type
                                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                  : 'text-gray-900 dark:text-white'
                              }`}
                            >
                              {ACTION_LABELS[type] || type}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => removeAction(index)}
                    className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {action.type !== ActionType.STOP_WORKFLOW && (
                  <ActionConfigPanel
                    action={action}
                    onChange={(config) => updateAction(index, { ...action, config })}
                  />
                )}
                {action.type === ActionType.STOP_WORKFLOW && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <StopCircle className="w-3 h-3" />
                    Workflow execution will stop here
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface ConditionalActionConfigProps {
  conditional: ConditionalConfig;
  onChange: (conditional: ConditionalConfig) => void;
}

export function ConditionalActionConfig({ conditional, onChange }: ConditionalActionConfigProps) {
  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <GitBranch className="w-4 h-4" />
        <span className="font-medium">If/Else Branch</span>
      </div>

      {/* Condition */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">IF condition:</p>
        <ConditionBuilder
          conditionGroup={conditional.condition}
          onChange={(condition) => onChange({ ...conditional, condition })}
          compact
        />
      </div>

      {/* Then Actions */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <ArrowRight className="w-3 h-3" />
        <span>THEN execute:</span>
      </div>
      <NestedActionList
        actions={conditional.thenActions}
        onChange={(thenActions) => onChange({ ...conditional, thenActions })}
        label="Then Actions"
        color="green"
      />

      {/* Else Actions */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <ArrowRight className="w-3 h-3" />
        <span>ELSE execute:</span>
      </div>
      <NestedActionList
        actions={conditional.elseActions || []}
        onChange={(elseActions) => onChange({ ...conditional, elseActions })}
        label="Else Actions (Optional)"
        color="red"
      />
    </div>
  );
}

interface SwitchActionConfigProps {
  switchConfig: SwitchConfig;
  onChange: (switchConfig: SwitchConfig) => void;
}

export function SwitchActionConfig({ switchConfig, onChange }: SwitchActionConfigProps) {
  const [showFieldDropdown, setShowFieldDropdown] = useState(false);

  const addCase = () => {
    const newCases: SwitchCase[] = [
      ...switchConfig.cases,
      { value: '', actions: [] },
    ];
    onChange({ ...switchConfig, cases: newCases });
  };

  const removeCase = (index: number) => {
    const newCases = [...switchConfig.cases];
    newCases.splice(index, 1);
    onChange({ ...switchConfig, cases: newCases });
  };

  const updateCase = (index: number, caseItem: SwitchCase) => {
    const newCases = [...switchConfig.cases];
    newCases[index] = caseItem;
    onChange({ ...switchConfig, cases: newCases });
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <GitBranch className="w-4 h-4" />
        <span className="font-medium">Switch/Case Branch</span>
      </div>

      {/* Field Selector */}
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          Switch on field:
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFieldDropdown(!showFieldDropdown)}
            className={`${selectClass} flex items-center justify-between text-left`}
          >
            <span className={switchConfig.field ? '' : 'text-gray-400 dark:text-gray-500'}>
              {switchConfig.field
                ? SWITCH_FIELD_OPTIONS.find((f) => f.value === switchConfig.field)?.label || switchConfig.field
                : 'Select a field...'}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showFieldDropdown ? 'rotate-180' : ''}`} />
          </button>
          {showFieldDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowFieldDropdown(false)} />
              <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {SWITCH_FIELD_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange({ ...switchConfig, field: option.value });
                      setShowFieldDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                      switchConfig.field === option.value
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
                <div className="border-t border-gray-100 dark:border-gray-600 px-3 py-2">
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="Or enter custom field path..."
                    value={SWITCH_FIELD_OPTIONS.find((f) => f.value === switchConfig.field) ? '' : switchConfig.field}
                    onChange={(e) => onChange({ ...switchConfig, field: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Cases */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Cases:</label>
          <Button variant="ghost" size="sm" onClick={addCase}>
            <Plus className="w-3 h-3 mr-1" />
            Add Case
          </Button>
        </div>

        {switchConfig.cases.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">
            No cases defined - click Add Case to create one
          </p>
        ) : (
          switchConfig.cases.map((caseItem, index) => (
            <div
              key={index}
              className="border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                  CASE {index + 1}:
                </span>
                <input
                  type="text"
                  className={`${inputClass} flex-1`}
                  placeholder="Value to match (e.g., 'in_progress', 'high')"
                  value={String(caseItem.value)}
                  onChange={(e) => updateCase(index, { ...caseItem, value: e.target.value })}
                />
                <button
                  onClick={() => removeCase(index)}
                  className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <NestedActionList
                actions={caseItem.actions}
                onChange={(actions) => updateCase(index, { ...caseItem, actions })}
                label={`Case "${caseItem.value || '...'}" Actions`}
                color="blue"
              />
            </div>
          ))
        )}
      </div>

      {/* Default Actions */}
      <div className="mt-3">
        <NestedActionList
          actions={switchConfig.defaultActions || []}
          onChange={(defaultActions) => onChange({ ...switchConfig, defaultActions })}
          label="Default Actions (if no case matches)"
          color="gray"
        />
      </div>
    </div>
  );
}

interface BranchingActionConfigProps {
  action: Action;
  onConditionalChange: (conditional: ConditionalConfig) => void;
  onSwitchChange: (switchConfig: SwitchConfig) => void;
}

export default function BranchingActionConfig({
  action,
  onConditionalChange,
  onSwitchChange,
}: BranchingActionConfigProps) {
  if (action.type === ActionType.CONDITIONAL) {
    const defaultConditional: ConditionalConfig = {
      condition: { logic: 'AND', conditions: [] },
      thenActions: [],
      elseActions: [],
    };
    return (
      <ConditionalActionConfig
        conditional={action.conditional || defaultConditional}
        onChange={onConditionalChange}
      />
    );
  }

  if (action.type === ActionType.SWITCH) {
    const defaultSwitch: SwitchConfig = {
      field: '',
      cases: [],
      defaultActions: [],
    };
    return (
      <SwitchActionConfig
        switchConfig={action.switch || defaultSwitch}
        onChange={onSwitchChange}
      />
    );
  }

  if (action.type === ActionType.STOP_WORKFLOW) {
    return (
      <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300">
          <StopCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Stop Workflow</span>
        </div>
        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
          When this action executes, the workflow will stop immediately and no further actions will run.
          This is useful inside conditional branches to exit early based on conditions.
        </p>
      </div>
    );
  }

  return null;
}
